// @flow
import React, { Component } from 'react';
import styled from 'react-emotion';
import consts, {
  colors,
  configStructure,
  fonts,
} from '../assets/scripts/constants';
import { decomposeQuery } from '../assets/scripts/urlComposer';
import ConfiguratorTop from './ConfiguratorTop';
import ConfiguratorBottom from './ConfiguratorBottom';
import ConfiguratorPrice from './ConfiguratorPrice';
import PaymentOptions from './PaymentOptions';
import fetchOptions from '../services/config.service';
import iconCheck from '../assets/images/icon_check.svg';
import Button from './Button';

/**
 * Styles
 */

const Wrapper = styled('div')`
  display: flex;
  flex-wrap: wrap;
  max-width: 1280px;
  margin: 0 auto;
  padding: ${consts.cellEm} 0 ${3 * consts.cell}em;
  font-size: 10px;
  font-family: ${fonts.primary};

  @media (min-width: 768px) {
    padding-top: ${3 * consts.cell}em;
    padding-left: ${consts.cellEm};
    padding-right: ${consts.cellEm};
  }
`;

/**
 * Component: Configurator
 *
 * The main wrapper for the configurator, holds the state.
 */
class Configurator extends Component<
  {},
  {
    configData: { [string]: Object },
    configOptions: { [string]: Object },
  }
> {
  state = {
    configData: {},
    configOptions: {},
  };

  componentDidMount() {
    fetchOptions()
      .then(configOptions => {
        let { configData } = this.state;
        // Check for data query and configure based on that.
        const parsedConfigData = decomposeQuery({
          configOptions,
        });
        configData = Object.keys(parsedConfigData).length
          ? parsedConfigData
          : configData;
        this.setState({ configData, configOptions });
      })
      .catch(e => {
        console.error(e);
        this.goToFinalPage('error');
      });
  }

  goToFinalPage = (type: string) => {
    const { configData, configOptions } = this.state;

    let newConfigOptions;

    if (type === 'error') {
      if (configOptions.finalPage && configOptions.finalPage.type === 'error') {
        return;
      }
      newConfigOptions = {
        finalPage: {
          type: 'error',
          header: null,
          content: (
            <React.Fragment>
              <h1>Virhe!</h1>
              <p>
                Konfiguraattorin valintojen noutaminen epäonnistui. Yritä ladata
                sivu uudelleen.
              </p>
            </React.Fragment>
          ),
        },
      };
    } else {
      // success
      if (
        configOptions.finalPage &&
        configOptions.finalPage.type === 'success'
      ) {
        return;
      }
      const reloadTarget = window.location.origin + window.location.pathname;
      newConfigOptions = {
        ...configOptions,
        finalPage: {
          type: 'success',
          header: null,
          content: (
            <React.Fragment>
              <img src={iconCheck} alt="" />
              <h1>
                {`${
                  configData.quantity &&
                  configData.quantity.value &&
                  configData.quantity.value > 1
                    ? 'Tuotteet'
                    : 'Tuote'
                }`}{' '}
                lisätty ostoskoriin!
              </h1>
              <p>Viimeistele tilaus ostoskorissa tai räätälöi lisää ovia.</p>
              <div>
                <Button
                  bgColor={colors.black}
                  onClick={() => {
                    window.location = '/ostoskori';
                  }}
                >
                  Siirry ostoskoriin
                </Button>
                {/* @todo If there is time, make a better reset. */}
                <Button
                  bgColor={colors.black}
                  onClick={
                    window.navigate
                      ? window.navigate.bind(window, reloadTarget)
                      : window.location.assign.bind(
                          window.location,
                          reloadTarget
                        )
                  }
                >
                  Räätälöi lisää ovia
                </Button>
              </div>
            </React.Fragment>
          ),
        },
      };
    }

    this.setState({ configOptions: newConfigOptions });
  };

  handleConfigChange = (data: Object) => {
    const { configData } = this.state;
    const {
      event: {
        nativeEvent,
        target: { parentNode, value },
      },
      slug,
      title,
    } = data;

    // Custom color is added to surface (pa_pinta) selection
    if (slug === 'custom_color') {
      this.setState({
        configData: {
          ...configData,
          pa_pinta: { ...configData.pa_pinta, [slug]: value },
        },
      });
      return;
    }

    // MaterialUI, please, halp.
    const element = parentNode
      ? parentNode.parentNode.parentNode
      : nativeEvent.target;
    const label = element.textContent;
    const id = +element.getAttribute('data-id');
    let description = element.getAttribute('data-description');
    description = description && description.length ? description : '';
    let price = +element.getAttribute('data-price');
    price = Number.isFinite(price) ? price : 0;
    let time = +element.getAttribute('data-time');
    time = Number.isFinite(time) ? time : 0;
    this.setState(
      {
        configData: {
          ...configData,
          [slug]: { description, id, label, price, time, title, value },
        },
      },
      () => {
        // If previous selections are changed, we need to empty the selections
        // following that selection.
        let currentIndex = -1;
        configStructure.some((config, i) => {
          if (config.slug === slug) {
            currentIndex = i;
            return true;
          }
          return false;
        });

        if (currentIndex === -1) {
          return;
        }

        // Checking the latest state, using this.state
        const cdKeys = Object.keys(this.state.configData);
        const len = cdKeys.length;
        const removeSelections = len - 1 - currentIndex;

        if (removeSelections > 0) {
          const newConfigData = { ...this.state.configData };
          for (let n = 1; n <= removeSelections; n += 1) {
            delete newConfigData[cdKeys[len - n]];
          }

          this.setState({
            configData: {
              ...newConfigData,
            },
          });
        }
      }
    );
  };

  render() {
    const {
      goToFinalPage,
      handleConfigChange,
      state: { configData, configOptions },
    } = this;

    return (
      <Wrapper>
        <ConfiguratorTop
          configData={configData}
          configOptions={configOptions}
          goToFinalPage={goToFinalPage}
          handleConfigChange={handleConfigChange}
        />
        <ConfiguratorBottom configData={configData} />
        <ConfiguratorPrice
          configData={configData}
          configOptions={configOptions}
        />
        <PaymentOptions mobile />
      </Wrapper>
    );
  }
}

export default Configurator;



// WEBPACK FOOTER //
// ./src/components/Configurator.js